/**
 * Thin fetch cache with 30-second TTL and in-flight deduplication.
 *
 * - cachedFetch(url, options) — drop-in for fetch(); GET responses are cached.
 *   Non-GET requests bypass the cache entirely.
 * - invalidateCache(url) — evict a single URL (call after mutations).
 * - invalidateCachePrefix(prefix) — evict all entries whose key starts with prefix.
 */

const TTL_MS = 30_000;

// { url -> { data, expiresAt } }
const cache = new Map();

// { url -> Promise } — prevents parallel identical requests
const inFlight = new Map();

export async function cachedFetch(url, options = {}) {
    const method = (options.method || 'GET').toUpperCase();

    // Only cache GET requests
    if (method !== 'GET') {
        const res = await fetch(url, options);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.json();
    }

    // Return cached result if still fresh
    const cached = cache.get(url);
    if (cached && Date.now() < cached.expiresAt) {
        return cached.data;
    }

    // Deduplicate in-flight requests for the same URL
    if (inFlight.has(url)) {
        return inFlight.get(url);
    }

    const promise = fetch(url, options)
        .then(res => {
            if (!res.ok) throw new Error(`Request failed: ${res.status}`);
            return res.json();
        })
        .then(data => {
            cache.set(url, { data, expiresAt: Date.now() + TTL_MS });
            inFlight.delete(url);
            return data;
        })
        .catch(err => {
            inFlight.delete(url);
            throw err;
        });

    inFlight.set(url, promise);
    return promise;
}

export function invalidateCache(url) {
    cache.delete(url);
    // Let in-flight requests finish naturally
}

export function invalidateCachePrefix(prefix) {
    for (const key of cache.keys()) {
        if (key.startsWith(prefix)) cache.delete(key);
    }
}
